import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgForm,
  ValidationErrors,
  Validator,
  FormGroup,
  FormControl,
} from '@angular/forms';
import moment from 'moment';
import { DatepickerDateCustomClasses } from 'ngx-bootstrap/datepicker';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker/bs-datepicker.config';
import { BsDatepickerValue } from 'webcommon/shared';
import { ApBaseComponent } from '../ap-base-component/ap-base-component';

// Navigate to the file for BsDatepickerValue
// for more notes on this alias
export type ApDatePickerValue = BsDatepickerValue;
@Component({
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ApDatePickerComponent),
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ApDatePickerComponent),
    },
  ],
  selector: 'ap-date-picker',
  styleUrls: [
    './ap-date-picker.component.less',
  ],
  templateUrl: './ap-date-picker.component.html',
})
export class ApDatePickerComponent extends ApBaseComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {

  @Input() form: NgForm;
  @Input() baseId?: string;
  @Input() labelName: string;
  @Input() showLabel = true;
  @Input() required: boolean;
  @Input() formGroupSubmitted?: boolean;
  // * `BsDatepickerConfig` that can be used to override the default config.
  // * **Note**: This will override any custom date format
  @Input() customBSConfig?: Partial<BsDatepickerConfig>;
  @Input() showRequiredOnly = false;
  @Input() disabled = false;
  @Input() formGroup: FormGroup;
  @Input() controlName: string;
  @Input() maxDate?: Date;
  @Input() minDate?: Date;

  // Ignore the time component of the date when selecting, which means when selecting dates,
  // you'll always get a value that's at the start of the day.
  // This matters when filtering stuff based on dates on the server.
  // This is opt-in for now, but it feels like it should probably be usable in most cases,
  // since this component doesn't allow the user to pick a time, and the bsDatepicker defaults to the current time.
  @Input() defaultToStartOfDay = false;
  // This is the same as defaultTimeToStart, but for the end of the day,
  // like if you're picking the end of a date range or something
  @Input() defaultToEndOfDay = false;

  // Default config that will be used for the date picker
  bsConfig: Partial<BsDatepickerConfig> = {
    adaptivePosition: true,
    containerClass: 'theme-emds-datepicker',
    dateInputFormat: 'M/D/YYYY',
    selectFromOtherMonth: true,
    showWeekNumbers: false,
  };

  date: ApDatePickerValue = undefined; // The value of the control. Should only be accessed via ngModel outside of this class
  error: string; // String representation of the last `ValidationErrors`

  id: string;  // Id used to identify the child components
  dateCustomClasses: DatepickerDateCustomClasses[];
  formGroupInstance: FormGroup;
  formControl: FormControl;
  isInitialized = false;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.setId();
    if (this.customBSConfig) {
      this.bsConfig = this.customBSConfig;
    }

    const now = new Date();
    this.dateCustomClasses = [
      { date: now, classes: ['emds-datepicker-current-date-background-color'] },
    ];
    this.setupFormGroup();
    this.isInitialized = true;
  }


  ngOnChanges(changes: SimpleChanges) {
    if (!changes || !this.isInitialized) { return; }

    if (changes.disabled) {
      this.updateIsDisabled();
    }
  }

  private updateIsDisabled(): void {
    if (this.disabled === !this.formControl.enabled) { return; }
    if (this.formControl.enabled) {
      this.formControl.disable();
    }
    else {
      this.formControl.enable();
    }
  }

  private setupFormGroup(): void {
    if (this.form) {
      this.formGroupInstance = this.form.form;
    }
    else if (this.formGroup) {
      this.formGroupInstance = this.formGroup;
    }
    else {
      this.formGroupInstance = new FormGroup({});
    }
    if (!this.controlName) { // Should be what NgForm used as the name previsouly
      this.controlName = this.id + '-input-name';
    }
    this.formControl = this.formGroupInstance.get(this.controlName) as FormControl;
    if (!this.formControl) {
      // Date is undefined at this point so just set to empty string
      this.formControl = new FormControl<ApDatePickerValue | ''>('', [this.validate]);
      this.formGroupInstance.addControl(this.controlName, this.formControl);
    }
  }

  onChange(date: ApDatePickerValue) {
    if (date && this.defaultToStartOfDay) {
      date = moment(date).startOf('day').toDate();
    } else if (date && this.defaultToEndOfDay) {
      date = moment(date).endOf('day').toDate();
    }
    this.propagateChange(date);
  }

  private propagateChange = (_: ApDatePickerValue) => {
    /* Will be overridden */
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  writeValue(obj: ApDatePickerValue): void {
    this.date = obj;
    const formControlValue = this.formControl.value;
    const currentValue = this.date ? this.date.getTime() : undefined;

    if ((formControlValue !== '' || currentValue) &&
        (formControlValue?.getTime ? formControlValue.getTime() : undefined) !== currentValue) {
      this.formControl.setValue(this.date);
    }
  }

  isRequired(): boolean {
    return !this.isShowRequriedOnly() && this.required;
  }

  isShowRequriedOnly(): boolean {
    return this.showRequiredOnly;
  }

  registerOnTouched(_: any): void {
    /* For mobile */
  }

  private isDate(value: any): boolean {
    return !isNaN(Date.parse(value));
  }

  // Sets the current `error` as a string representation of the `ValidationErrors`
  private setErrors(error: string): ValidationErrors {
    this.error = error;

    return { error };
  }

  private clearError(): null {
    this.error = '';

    return null;
  }

  private setId() {
    this.id = this.baseId + '-date-picker';
  }

  onApplyDatePickerStyle(): any {
    const obj: any = {};
    if (this.required || this.isShowRequriedOnly()) {
      obj.backgroundColor = '#fafad2';
    }
    return obj;
  }

  // TODO: move this comment to test cases
  /**
   * ## Validates the form control
   * ## Use cases:
   * ---
   * ### Case 0:
   *    Required: false
   *    Value: '' or null or undefined
   *
   *    Result: null
   * ---
   * ### Case 1:
   *    Required: false
   *    Value: valid date
   *
   *    Result: null
   * ---
   * ### Case 2:
   *    Required: false
   *    Value: invalid date
   *
   *    Result: error like 'date is invalid'
   * ---
   * ### Case 3:
   *    Required: true
   *    Value: '' or null or undefined
   *
   *    Result: error like 'required field'
   * ---
   * ### Case 4:
   *    Required: true
   *    Value: valid date
   *
   *    Result: null
   * ---
   * ### Case 5:
   *    Required: true
   *    Value: invalid date
   *
   *    Result: error like 'date is invalid'
   */
  // ignore the passed control since we may get a formGroup when using reactive forms
  validate = (): ValidationErrors | null => {
    if (!this.formControl) { return this.clearError(); }
    if (!this.formControl.value || !this.isDate(this.formControl.value)) {
      if (this.isShowRequriedOnly()) return this.clearError();
      else {
        if (!this.formControl.value) {
          return this.required ? this.setErrors(this.labelName + ' is a required field') : this.clearError();
        } else {
          return this.setErrors('Date is invalid');
        }
      }
    }

    return this.clearError();
  }

  validateCharacter(event: KeyboardEvent): KeyboardEvent | false {
    if (!isNaN(event.key as any)) {
      return event;
    } else if (event.key === '/' || event.key === '.') {
      return event;
    } else {
      return false;
    }
  }
}
